import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ContractIllustration, ShieldIllustration, LockIllustration, CardIllustration, CookieIllustration, ScaleIllustration,
} from "../components/legal/LegalIllustrations";
import { useIsMobile } from "../hooks/useIsMobile";

interface Category { path: string; illustration: React.ReactNode; titleFr: string; titleEn: string; descFr: string; descEn: string }

const CATEGORIES: Category[] = [
  {
    path: "/terms", illustration: <ContractIllustration />,
    titleFr: "Conditions d'utilisation", titleEn: "Terms of Use",
    descFr: "Ce que tu acceptes en utilisant OLTODO, et ce qu'on s'engage à te fournir.",
    descEn: "What you agree to by using OLTODO, and what we commit to providing you.",
  },
  {
    path: "/community-rules", illustration: <ShieldIllustration />,
    titleFr: "Règles de la communauté", titleEn: "Community Rules",
    descFr: "Ce qui est autorisé, ce qui ne l'est pas, et comment on modère.",
    descEn: "What's allowed, what isn't, and how we moderate.",
  },
  {
    path: "/privacy-policy", illustration: <LockIllustration />,
    titleFr: "Confidentialité", titleEn: "Privacy",
    descFr: "Les informations que nous recevons et l'usage qu'on en fait.",
    descEn: "The information we receive and how we use it.",
  },
  {
    path: "/payments-policy", illustration: <CardIllustration />,
    titleFr: "Paiements et commissions", titleEn: "Payments and Commissions",
    descFr: "Comment marchent les paiements, séquestres, remboursements et virements.",
    descEn: "How payments, escrow, refunds and payouts work.",
  },
  {
    path: "/cookie-policy", illustration: <CookieIllustration />,
    titleFr: "Cookies", titleEn: "Cookies",
    descFr: "Ce qu'on stocke dans ton navigateur, et pourquoi.",
    descEn: "What we store in your browser, and why.",
  },
  {
    path: "/legal-notices", illustration: <ScaleIllustration />,
    titleFr: "Mentions légales", titleEn: "Legal Notices",
    descFr: "Qui édite OLTODO, où nous contacter, et le droit applicable.",
    descEn: "Who publishes OLTODO, how to reach us, and governing law.",
  },
];

// Notre engagement — trois affirmations réellement vraies (pas du remplissage marketing) :
// vérification obligatoire déjà en place, messages chiffrés au repos, décision de modération
// toujours motivée et contestable par email. Inspiré du bandeau "Notre méthode de travail" du
// Centre de confidentialité Facebook, mais avec du contenu propre à OLTODO plutôt que copié.
const COMMITMENTS = [
  {
    titleFr: "Des profils vérifiés", titleEn: "Verified profiles",
    descFr: "Un selfie est exigé avant de pouvoir participer, créer ou contacter qui que ce soit. Une pièce justificative est facultative.",
    descEn: "A selfie is required before you can join, create, or contact anyone. A supporting document is optional.",
  },
  {
    titleFr: "Des messages protégés", titleEn: "Protected messages",
    descFr: "Le contenu de tes conversations est chiffré au repos dans notre base, jamais lisible en clair.",
    descEn: "Your conversations are encrypted at rest in our database, never readable in plain text.",
  },
  {
    titleFr: "Une modération explicable", titleEn: "Explainable moderation",
    descFr: "Chaque décision de modération peut être contestée en écrivant à contact@oltodo.com.",
    descEn: "Every moderation decision can be challenged by writing to contact@oltodo.com.",
  },
];

export default function LegalCenter() {
  const navigate = useNavigate();
  const [lang, setLang] = useState<"fr" | "en">("fr");
  const isMobile = useIsMobile();

  return (
    <div style={{ height: "100vh", overflowY: "auto", overflowX: "hidden", backgroundColor: "#FAFAFA" }}>
      <div style={{ backgroundColor: "white", borderBottom: "1px solid #F0F0F0", padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 10 }}>
        <button onClick={() => navigate(-1)} style={{ border: "none", background: "none", cursor: "pointer", color: "#0a0a0a", fontSize: 14, fontWeight: 700, padding: 0, display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          {lang === "fr" ? "Retour" : "Back"}
        </button>
        <div style={{ display: "flex", gap: 6 }}>
          {(["fr", "en"] as const).map(l => (
            <button key={l} onClick={() => setLang(l)} style={{ border: "1px solid #E0E0E0", borderRadius: 20, padding: "5px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", backgroundColor: lang === l ? "#0a0a0a" : "white", color: lang === l ? "white" : "#0a0a0a" }}>
              {l.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 980, margin: "0 auto", padding: isMobile ? "40px 20px 80px" : "72px 24px 100px" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <img src="/oltodo.png" alt="OLTODO" style={{ width: 40, height: 40, objectFit: "contain" }} />
        </div>
        <p style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", color: "#999", marginBottom: 14, textAlign: "center" }}>
          {lang === "fr" ? "Conditions générales et règlements" : "Terms and Policies"}
        </p>
        <h1 style={{ fontSize: isMobile ? 32 : 44, fontWeight: 800, letterSpacing: -1, marginBottom: 16, maxWidth: 640 }}>
          {lang === "fr" ? "Tout ce que tu dois savoir, réuni au même endroit." : "Everything you need to know, in one place."}
        </h1>
        <p style={{ fontSize: 16, color: "#666", lineHeight: 1.7, maxWidth: 560, marginBottom: 56 }}>
          {lang === "fr"
            ? "Comment fonctionne OLTODO, ce qui est autorisé, comment tes données sont utilisées, comment les paiements sont traités. Le détail, pas juste un sommaire."
            : "How OLTODO works, what's allowed, how your data is used, how payments are handled. The detail, not just a table of contents."}
        </p>

        <p style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: "#999", marginBottom: 18 }}>
          {lang === "fr" ? "Notre engagement" : "Our commitment"}
        </p>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 16, marginBottom: 64 }}>
          {COMMITMENTS.map((c, i) => (
            <div key={i} style={{ backgroundColor: "white", border: "1px solid #EFEFEF", borderRadius: 18, padding: 22 }}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{lang === "fr" ? c.titleFr : c.titleEn}</div>
              <div style={{ fontSize: 13.5, color: "#666", lineHeight: 1.6 }}>{lang === "fr" ? c.descFr : c.descEn}</div>
            </div>
          ))}
        </div>

        <p style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: "#999", marginBottom: 18 }}>
          {lang === "fr" ? "Tous les règlements" : "All policies"}
        </p>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 16 }}>
          {CATEGORIES.map(cat => (
            <div
              key={cat.path}
              onClick={() => navigate(cat.path)}
              style={{ backgroundColor: "white", border: "1px solid #EFEFEF", borderRadius: 20, padding: 20, cursor: "pointer", display: "flex", flexDirection: "column" }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = "#D5D5D5")}
              onMouseLeave={e => (e.currentTarget.style.borderColor = "#EFEFEF")}
            >
              <div style={{ backgroundColor: "#F5F5F5", borderRadius: 16, padding: 16, marginBottom: 18, height: 110, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ width: 110 }}>{cat.illustration}</div>
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>{lang === "fr" ? cat.titleFr : cat.titleEn}</div>
              <div style={{ fontSize: 13, color: "#666", lineHeight: 1.6, marginBottom: 14, flex: 1 }}>{lang === "fr" ? cat.descFr : cat.descEn}</div>
              <div style={{ fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                {lang === "fr" ? "En savoir plus" : "Learn more"}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18"/></svg>
              </div>
            </div>
          ))}
        </div>

        <p style={{ fontSize: 13, color: "#999", marginTop: 56, lineHeight: 1.7 }}>
          {lang === "fr"
            ? "Une question qui n'a pas de réponse ici ? Écris-nous à "
            : "A question that isn't answered here? Write to us at "}
          <a href="mailto:contact@oltodo.com" style={{ color: "#0a0a0a", fontWeight: 700 }}>contact@oltodo.com</a>.
        </p>
      </div>
    </div>
  );
}
